import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { withFormik, FormikProps } from 'formik';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import intersection from 'lodash/intersection';
import { History } from 'history';
import {
  getCCExpiryYear,
  expiryDateModifier,
  creditCardNumberModifier,
  cvvModifier,
} from '../../utilities';
import C from '../../constants';
import Toggle from '../../atoms/Toggle';
import { Input } from '../../atoms/Input';
import Label from '../../atoms/Label';
import Button from '../../components/Button/Button';
import Spinner from '../../foundations/Spinner';
import {
  ValidateCardParams,
  clearCardValidations,
  addNewCard,
  validateCardAsync,
  getCardTypeAsync,
} from '../../redux/payment_methods';

import { payInvoiceAsync } from '../../redux/invoices/index';

import { paymentFormSchema } from '../../utilities/formSchemas';

import {
  ButtonWrapper,
  DefaultPayment,
  PaymentMethodCardImage,
  PaymentMethodColumn,
  PaymentMethodRow,
  PaymentMethodError,
  PaymentMethodFieldWrapper,
} from './styles';
import { Prompt } from '../../components/Modal';

type OwnProps = {
  params: {
    retailerId: string;
    supplierId?: string;
    paymentRequestId?: string;
  };
  history: History;
  addNewCard: (
    payload,
    retailerId: string,
    paymentRequestId?: string,
    token?: string
  ) => void;
  paymentType?: string[];
  location?: {
    query: { [key: string]: string };
  };
  purchaserId?: string;
  cancelUrl?: string;
  source?: string;
  onSuccess?: () => void;
  validateCardAsync: (cardDetails: ValidateCardParams) => void;
  clearCardValidations: () => void;
  fetchPurchaserDetailsAsync?: (supplierId: string, retailerId: string) => void;
  getCardTypeAsync: (cardDetails: any) => any;
  confirmModal?: {
    text: string;
    confirmLabel: string;
  };
};

export type Values = {
  cardName: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardType: string;
  defaultPayment: boolean;
};

const isValidPayment = (validTypes, paymentType) =>
  intersection(validTypes, paymentType).length > 0;

const formatCardErrors = validity => {
  const errors: any = {};
  if (validity.isExpired) {
    errors.expiryDate = 'Card is expired';
  }
  if (!validity.validCardNumber) {
    errors.cardNumber = 'Card number is not valid';
  }
  if (!validity.validCvv) {
    errors.cvv = 'CVV is not valid';
  }
  if (!validity.validExpiryMonth || !validity.validExpiryYear) {
    errors.expiryDate = 'Expiry date is not valid';
  }
  return errors;
};

export const CreditCardField = (props: any) => {
  const { label, errors, tag, formatValue } = props;
  const fieldProps = {
    ...props,
  };

  if (formatValue) {
    fieldProps.onChange = (_blank: string, value: string) => {
      const newValue = formatValue(value);
      props.setFieldValue(tag, newValue);
    };
  }

  return (
    <PaymentMethodFieldWrapper>
      <Label label={label} htmlFor={tag} />
      <Input {...fieldProps} />
      {errors[tag] && <PaymentMethodError>{errors[tag]}</PaymentMethodError>}
    </PaymentMethodFieldWrapper>
  );
};

type Props = OwnProps & FormikProps<Values>;
const NewCreditCardComponent = (props: Props) => {
  const {
    handleSubmit,
    setFieldValue,
    values,
    isSubmitting,
    errors,
    validateForm,
    cancelUrl,
    location,
    params,
    paymentType,
    confirmModal,
  } = props;

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const hasConfirmation = !!confirmModal;
  const { cardNumber, cardName, expiryDate, cvv, defaultPayment } = values;
  const { supplierId, retailerId } = params;

  const isInvoice = location?.query?.invoiceId;

  const handleInputChange = (tag: string, value: string) => {
    setFieldValue(tag, value);
  };

  const handleToggleChange = (value: boolean) => {
    setFieldValue('defaultPayment', value);
  };

  const isValidPaymentType = isValidPayment(
    C.VALID_CARD_PAYMENT_TYPES,
    paymentType
  );

  useEffect(() => {
    (async () => {
      const str = values.cardNumber.toString();
      const cardTypeResult = await props.getCardTypeAsync(str);
      setFieldValue('cardType', cardTypeResult?.value?.data?.toLowerCase());
    })();
  }, [values.cardNumber]);

  useEffect(() => {
    if (supplierId && retailerId && props.fetchPurchaserDetailsAsync) {
      props.fetchPurchaserDetailsAsync(supplierId, retailerId);
    }
  }, [supplierId, retailerId]);

  useEffect(() => {
    if (props.location?.query?.returnUrl) {
      handleToggleChange(true);
    }
  }, [props.location]);

  useEffect(
    () => () => {
      clearCardValidations();
    },
    []
  );

  return (
    <form onSubmit={handleSubmit}>
      <PaymentMethodRow>
        <CreditCardField
          {...props}
          label="Name on card"
          tag="cardName"
          size="max"
          onChange={handleInputChange}
          value={cardName}
          hasError={errors.cardName}
        />
      </PaymentMethodRow>
      <PaymentMethodRow>
        <CreditCardField
          {...props}
          label="Card number"
          tag="cardNumber"
          size="max"
          formatValue={creditCardNumberModifier}
          value={cardNumber}
          hasError={errors.cardNumber}
        />
        {values.cardType && (
          <PaymentMethodCardImage
            src={`${process.env.IMAGE_URL}/${values.cardType}.svg`}
            alt="New Credit Card Icon"
          />
        )}
      </PaymentMethodRow>
      <PaymentMethodRow>
        <PaymentMethodColumn width="half">
          <CreditCardField
            {...props}
            label="Expiry date"
            tag="expiryDate"
            size="max"
            placeholder="MM/YY"
            formatValue={expiryDateModifier}
            value={expiryDate}
            hasError={errors.expiryDate}
          />
        </PaymentMethodColumn>
        <PaymentMethodColumn width="half">
          <CreditCardField
            {...props}
            label="CVV"
            tag="cvv"
            type="number"
            size="max"
            value={cvv}
            formatValue={cvvModifier}
            hasError={errors.cvv}
          />
        </PaymentMethodColumn>
      </PaymentMethodRow>
      {isValidPaymentType && (
        <DefaultPayment>
          Make this default payment
          <Toggle
            checked={defaultPayment || false}
            handleChange={handleToggleChange}
            uniqueId="defaultPayment"
            tag="defaultPayment"
            borderless
          />
        </DefaultPayment>
      )}
      {isInvoice && (
        <p>
          This card will be saved as the default payment for future orders with
          with this supplier.
        </p>
      )}
      <ButtonWrapper>
        {cancelUrl && (
          <Link
            to={{
              pathname: cancelUrl,
              query: location?.query,
            }}
          >
            <Button variant="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
          </Link>
        )}
        <Button
          type={hasConfirmation ? 'button' : 'submit'}
          disabled={isSubmitting}
          onClick={() => {
            if (hasConfirmation) {
              validateForm().then(validateErrors => {
                const fieldErrors = Object.keys(validateErrors);
                if (fieldErrors.length === 0) {
                  setShowConfirmModal(true);
                }
              });
            }
          }}
        >
          {isSubmitting ? (
            <Spinner size="M" />
          ) : (
            <>
              {isInvoice ? 'Save card and pay invoice' : 'Save payment method'}
            </>
          )}
        </Button>
      </ButtonWrapper>
      {showConfirmModal && hasConfirmation && (
        <Prompt
          title={confirmModal.confirmLabel}
          actionLabel={confirmModal.confirmLabel}
          onRequestClose={() => {
            setShowConfirmModal(false);
          }}
          onAccept={handleSubmit}
          loading={isSubmitting}
          isOpen={showConfirmModal}
          actionType="submit"
        >
          {confirmModal.text}
        </Prompt>
      )}
    </form>
  );
};

export const NewCreditCardComponentEnhanced = withFormik<OwnProps, Values>({
  validationSchema: paymentFormSchema,
  validateOnChange: false,
  validateOnBlur: false,
  handleSubmit: async (values, formikBag: any) => {
    const { props, setErrors } = formikBag;
    const {
      fetchPurchaserDetailsAsync,
      params,
      paymentType,
      purchaserId,
      location,
      source = '',
    } = props;
    const { supplierId, retailerId } = params;
    const token = location?.query?.token;
    const paymentRequestId = location?.query?.paymentRequestId;

    const [expiryMonth] = values.expiryDate.split('/');

    const cardDetails = {
      userId: retailerId,
      source,
      name: values.cardName,
      type: 'card',
      number: values.cardNumber.toString().replace(/\s/g, ''),
      expiryMonth,
      expiryYear: getCCExpiryYear(values.expiryDate),
      issuer: values.cardType === 'americanexpress' ? 'amex' : values.cardType,
      cvv: values.cvv,
      purchaserId,
      defaultPayment:
        values.defaultPayment &&
        isValidPayment(C.VALID_CARD_PAYMENT_TYPES, paymentType),
    };

    const validateRes = await props.validateCardAsync({
      ...cardDetails,
      type: values.cardType === 'americanexpress' ? 'ae' : values.cardType,
      expireMonth: cardDetails.expiryMonth,
      expireYear: cardDetails.expiryYear,
    });

    const validity = validateRes?.value?.data;

    let errors;
    if (validity) {
      errors = formatCardErrors(validity);
    }

    if (!isEmpty(errors)) {
      setErrors(errors);
      return;
    }

    const result = await props.addNewCard(
      cardDetails,
      props.params.retailerId,
      location?.query?.paymentRequestId,
      location?.query?.token
    );

    const paymentMethodId = result?.value?.data?.id;

    props.clearCardValidations();

    if (
      paymentMethodId &&
      location?.query?.invoiceId &&
      token &&
      paymentRequestId
    ) {
      await props.payInvoiceAsync({
        id: location.query.invoiceId,
        paymentRequestId,
        paymentMethodId,
        token,
      });
    }

    if (props.onSuccess) {
      props.onSuccess();
    } else if (props.location?.query?.returnUrl && values.defaultPayment) {
      const url = new URL(props.location.query.returnUrl, process.env.APP_URL);
      const { pathname, search } = url;
      const parsedQuery = qs.parse(search.replace('?', ''));

      if (supplierId && retailerId && fetchPurchaserDetailsAsync) {
        await fetchPurchaserDetailsAsync(supplierId, retailerId);
      }

      props.history.push({
        pathname,
        query: {
          showModal: true,
          ...parsedQuery,
        },
      });
    } else if (supplierId && retailerId) {
      props.history.push(
        `/retailer/${retailerId}/supplier/${supplierId}/settings/payment`
      );
    } else if (retailerId) {
      props.history.push(
        `/dashboard/settings/payment-methods/retailer/${retailerId}`
      );
    }
  },
  mapPropsToValues: () => ({
    cardName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardType: '',
    defaultPayment: false,
  }),
})(NewCreditCardComponent);

const mapStateToProps = state => ({});

const mapDispatchToProps = {
  addNewCard,
  clearCardValidations,
  getCardTypeAsync,
  validateCardAsync,
  payInvoiceAsync,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewCreditCardComponentEnhanced);
